import { shallowEqual, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { PageNotFound, SplashScreen } from '@biznessforce/ui-lib';
import { hasAppPermissions } from '@constants/utils';

type AuthRouteState = {
  auth: {
    authToken: string | null | undefined;
    isAuthChecked: boolean;
    account?: {
      authorities?: string[];
    };
  };
};

// Check the App is Authenticated or Not
const AuthenticatedRoute = ({ component: Component, ...rest }) => {
  const { hasAuthToken, isAuthChecked } = useSelector(
    ({ auth }: AuthRouteState) => ({
      hasAuthToken: auth.authToken != null,
      isAuthChecked: auth.isAuthChecked,
    }),
    shallowEqual
  );

  const location = useLocation();

  return (
    <Route
      {...rest}
      render={() => {
        if (hasAuthToken && !isAuthChecked) {
          return <SplashScreen />;
        }

        return hasAuthToken ? (
          <>
            <Component />
          </>
        ) : (
          <Redirect
            to={{
              pathname: '/auth/login',
              search: `?redirect=${location.pathname || '/'}`,
              state: { from: location.pathname || '/' },
            }}
          />
        );
      }}
    />
  );
};

// Allow Only Authorized Routes
const AuthorizedRoute = ({ component: Component, allowedRoles, ...rest }) => {
  const { account } = useSelector(
    ({ auth: { account } }: AuthRouteState) => ({ account }),
    shallowEqual
  );

  return (
    <Route
      {...rest}
      render={(props) => {
        return !allowedRoles || hasAppPermissions(account?.authorities, allowedRoles) ? (
          <Component {...props} />
        ) : (
          <PageNotFound onBackClick={() => {}} />
        );
      }}
    />
  );
};

// RouteSwitch with pageNotFound Handled
const RouterSwitch = ({ children }) => (
  <Switch>
    {children}
    {/* Page Not Found */}
    <Route path="*">
      <PageNotFound onBackClick={() => {}} />
    </Route>
  </Switch>
);

export { AuthenticatedRoute, AuthorizedRoute, RouterSwitch };
